<template>
  <div class="card mb-3">
    <div class="card-header">
      <h5 class="mb-0">{{ $t("soldera_inventory.purchase_details") }}</h5>
    </div>
    <div class="card-body">
      <div class="row">
        <div class="col-lg-6">
          <CounterpartyPicker
            ref="counterpartyPicker"
            v-model="counterpartyModel"
            :registration-authority="registrationAuthority"
            @select:company="terms.handleCompanySelected"
          />
          <div class="mb-3">
            <label :for="`${uid}-country`" class="form-label">{{
              $t("soldera_inventory.billing_country")
            }}</label>
            <SearchableSelectComponent
              :inputId="`${uid}-country`"
              v-model="counterpartyCountry"
              :options="ALL_COUNTRIES"
              :placeholder="$t('soldera_inventory.billing_country')"
            />
          </div>
        </div>
        <div class="col-lg-6">
          <div class="mb-3">
            <label :for="`${uid}-emails`" class="form-label">{{
              $t("soldera_inventory.counterparty_emails")
            }}</label>
            <textarea
              :id="`${uid}-emails`"
              v-model="counterpartyEmails"
              class="form-control"
              rows="2"
              maxlength="1000"
              data-testid="counterparty-emails"
            ></textarea>
            <div class="form-text">
              {{ $t("soldera_inventory.counterparty_emails_hint") }}
            </div>
          </div>
          <div class="mb-3">
            <label :for="`${uid}-price`" class="form-label">{{
              $t("soldera_inventory.price")
            }}</label>
            <div class="input-group">
              <input
                :id="`${uid}-price`"
                v-model.number="unitPrice"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': showPriceError }"
                min="0"
                step="0.01"
                data-testid="unit-price"
              />
              <span class="input-group-text">EUR/MWh</span>
            </div>
            <div
              v-if="showPriceError"
              class="alert alert-danger py-2 px-3 mt-2 mb-0 small"
              data-testid="price-error"
            >
              {{ $t("soldera_inventory.blocked.no_price") }}
            </div>
          </div>
          <div class="form-text">{{ priceHint }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, toRefs, useId, useTemplateRef } from "vue";

import CounterpartyPicker from "@/components/common/CounterpartyPicker.vue";
import SearchableSelectComponent from "@/components/common/SearchableSelectComponent.vue";
import { ALL_COUNTRIES } from "@/constants";
import type { RegistrationAuthority } from "@/utils/registryAccounts";

import { useOfferTerms } from "./useOfferTerms";

/**
 * The buyer's offer terms, the same form on the Spot and Forward pages and backed by the terms the
 * layout provides, so what the buyer enters follows them between the two. The page says which certificate scheme the
 * registry account must fit and whether there is anything to price yet.
 */
const props = defineProps<{
  registrationAuthority: RegistrationAuthority | null;
  /** Whether the page has a selection, so a missing price is worth pointing out. */
  hasSelection: boolean;
  priceHint: string;
}>();

const terms = useOfferTerms();
const {
  counterpartyModel,
  counterpartyCountry,
  counterpartyEmails,
  unitPrice,
  hasPrice,
} = toRefs(terms);
const uid = useId();
const counterpartyPicker =
  useTemplateRef<InstanceType<typeof CounterpartyPicker>>("counterpartyPicker");

const showPriceError = computed(() => props.hasSelection && !hasPrice.value);

/** Marks the counterparty fields touched and tells whether they are valid. */
const validate = (): boolean =>
  counterpartyPicker.value?.touchAndValidate() ?? false;

defineExpose({ validate });
</script>
